import { getElementScale } from './element-scale'

export const calculatorPopperSurfaceStyles = {
  '& .MuiAutocomplete-listbox': {
    maxHeight: 'calc(var(--calculator-popup-height, 100vh) - 8px)',
  },
  '& .MuiPickerPopper-paper, & .MuiPickersPopper-paper': {
    maxHeight: 'var(--calculator-popup-height, none)',
    overflowY: 'auto',
  },
}

export const calculatorPopperBoundsModifier = {
  name: 'calculatorBounds',
  enabled: true,
  phase: 'beforeWrite' as const,
  requires: [ 'computeStyles' ],
  fn: ({ state }: CalculatorPopperBoundsParams) => {
    const popper = state.elements.popper
    const embedded = popper.closest<HTMLElement>('.demo-calculator-overlays')
    const container = embedded || (popper.offsetParent instanceof HTMLElement ? popper.offsetParent : document.body)
    const bounds = container.getBoundingClientRect()
    const reference = state.elements.reference
    const anchor = reference.getBoundingClientRect()
    const referenceElement = reference instanceof HTMLElement ? reference : reference.contextElement
    const referenceScale = referenceElement ? getElementScale(referenceElement) : 1
    const parentScale = getElementScale(container)
    const scale = embedded ? parentScale : referenceScale
    if (!scale) return

    const left = embedded ? Math.max(0, bounds.left) + 8 : 8
    const right = embedded ? Math.min(window.innerWidth, bounds.right) - 8 : window.innerWidth - 8
    const top = embedded ? Math.max(0, bounds.top) + 8 : 8
    const bottom = embedded ? Math.min(window.innerHeight, bounds.bottom) - 8 : window.innerHeight - 8
    const autocomplete = popper.classList.contains('MuiAutocomplete-popper')
    const datePicker = popper.classList.contains('MuiPickerPopper-root') || popper.classList.contains('MuiPickersPopper-root')
    const hasScrollableSurface = autocomplete || datePicker
    const naturalWidth = Math.max(state.rects.popper.width, popper.scrollWidth) * scale
    const width = Math.min(autocomplete ? anchor.width : naturalWidth, right - left)
    const height = Math.max(state.rects.popper.height, popper.scrollHeight) * scale
    const preferredPlacement = state.options.placement.split('-')[0]
    const vertical = preferredPlacement !== 'left' && preferredPlacement !== 'right'
    const below = Math.max(0, bottom - Math.max(anchor.bottom, top))
    const above = Math.max(0, Math.min(anchor.top, bottom) - top)
    const placeAbove = preferredPlacement === 'top'
      ? above >= height || above > below
      : below < height && above > below
    const before = Math.max(0, Math.min(anchor.left, right) - left)
    const after = Math.max(0, right - Math.max(anchor.right, left))
    const placeBefore = preferredPlacement === 'left'
      ? before >= width || before > after
      : after < width && before > after
    const horizontalSpace = placeBefore ? before : after
    const verticalSpace = placeAbove ? above : below
    const availableWidth = vertical ? right - left : horizontalSpace
    const availableHeight = vertical ? verticalSpace : bottom - top
    const popupWidth = Math.min(width, availableWidth)
    const popupHeight = Math.min(height, availableHeight)
    const alignEnd = state.options.placement.endsWith('-end')
    const verticalPlacement = placeAbove ? 'top' : 'bottom'
    const horizontalPlacement = placeBefore ? 'left' : 'right'
    const actualPlacement = vertical ? verticalPlacement : horizontalPlacement
    const alignmentSuffix = alignEnd ? '-end' : '-start'
    const suffix = state.options.placement.includes('-') ? alignmentSuffix : ''
    const offset = state.modifiersData.offset?.[`${actualPlacement}${suffix}`] || { x: 0, y: 0 }
    const alignedX = alignEnd ? anchor.right - popupWidth : anchor.left
    const horizontalX = placeBefore ? anchor.left - popupWidth : anchor.right
    const verticalY = placeAbove ? anchor.top - popupHeight : anchor.bottom
    const alignedY = alignEnd ? anchor.bottom - popupHeight : anchor.top
    const desiredX = (vertical ? alignedX : horizontalX) + offset.x * scale
    const desiredY = (vertical ? verticalY : alignedY) + offset.y * scale
    const fixed = !embedded && state.styles.popper.position === 'fixed'
    const originX = fixed ? 0 : bounds.left
    const originY = fixed ? 0 : bounds.top
    const x = (Math.max(left, Math.min(desiredX, right - popupWidth)) - originX) / scale
    const y = (Math.max(top, Math.min(desiredY, bottom - popupHeight)) - originY) / scale

    popper.style.setProperty('--calculator-popup-height', `${Math.max(0, availableHeight / scale - Math.abs(offset.y))}px`)

    state.styles.popper = {
      ...state.styles.popper,
      left: '0',
      top: '0',
      right: 'auto',
      bottom: 'auto',
      transform: `translate(${x}px, ${y}px)`,
      maxWidth: `${Math.max(0, availableWidth) / scale}px`,
      maxHeight: `${availableHeight / scale}px`,
      overflowX: hasScrollableSurface ? 'visible' : 'auto',
      overflowY: hasScrollableSurface ? 'visible' : 'auto',
      ...(autocomplete && { width: `${anchor.width / scale}px` }),
      ...(!embedded && { zoom: String(scale / parentScale) }),
    }
    state.attributes.popper = {
      ...state.attributes.popper,
      'data-popper-placement': `${actualPlacement}${suffix}`,
    }
  },
}
